import {
  TIndirectTaxTaxClass,
  TIndirectTaxTaxClassReason,
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionType,
} from '@commenda/api/indirect-tax/transactions/types';
import { IndirectTaxRegion as TIndirectTaxTransactionRegion } from '../../common';
import { IndirectTaxFilingStatus } from '../../filings/types/types';

export {
  TIndirectTaxTransactionRegion,
  TIndirectTaxTransactionStatus,
  TIndirectTaxTransactionType,
  TIndirectTaxTaxClass as TaxClass,
  TIndirectTaxTaxClassReason as TaxClassReason,
};

// FRONTEND ONLY TYPES

export enum TIndirectTaxTransactionVatStatus {
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
  PENDING = 'PENDING',
}

export enum TIndirectTaxTransactionSupplyType {
  INTRA_COMMUNITY = 'INTRA_COMMUNITY',
  DOMESTIC = 'DOMESTIC',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
}

export enum TIndirectTaxTransactionDocumentType {
  SALE = 'SALE',
  CREDIT_NOTE = 'CREDIT_NOTE',
  DEBIT_NOTE = 'DEBIT_NOTE',
}

export enum TIndirectTaxTransactionTableFilters {
  PERIOD = 'PERIOD',
  REPORTING_DATE = 'REPORTING_DATE',
  STATUS = 'STATUS',
  TRANSACTION_TYPE = 'TRANSACTION_TYPE',
  DESTINATION_COUNTRIES = 'DESTINATION_COUNTRIES',
  DESTINATION_STATES = 'DESTINATION_STATES',
  ORIGIN_COUNTRIES = 'ORIGIN_COUNTRIES',
  SOURCE_PLATFORM = 'SOURCE_PLATFORM',
  MARKETPLACE = 'MARKETPLACE',
}

export interface TIndirectTaxTransactionCustomer {
  id: string;
  name: string;
  email: string;
}

// DETAIL PAGE TYPES

export enum TIndirectTaxTaxBasis {
  TAX_EXCLUSIVE = 'TAX_EXCLUSIVE',
  TAX_INCLUSIVE = 'TAX_INCLUSIVE',
}

export interface TIndirectTaxJurisdictionBreakdown {
  jurisdiction: string;
  jurisdictionType: string;
  exempt: number;
  nonTaxable: number;
  taxable: number;
  marketplace: number;
  taxOrFees: number;
  rate: string;
}

export interface TIndirectTaxLineItemTaxLogic {
  taxCalculationEnabled: boolean;
  registrationId?: string;
  customerTaxable: boolean;
  customerId?: string;
  productTaxable: boolean;
  productId?: string;
}

export interface TIndirectTaxLineItemTaxLogicHandlers {
  onViewRegistration?: (registrationId: string) => void;
  onViewCustomer?: (customerId: string) => void;
  onViewProduct?: (productId: string) => void;
}

export interface TIndirectTaxLineItemTaxLogicRow {
  question: string;
  answer: boolean;
  linkLabel: string;
  onView?: () => void;
}

export interface TIndirectTaxTransactionLineItem {
  id: string;
  productName: string;
  productCode: string;
  productSku: string;
  productId?: string;
  taxCode: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  tax: number;
  lineTotal: number;
  taxIncluded: boolean;
  taxBreakdown?: TIndirectTaxJurisdictionBreakdown[];
  taxLogic?: TIndirectTaxLineItemTaxLogic;
}

export interface TIndirectTaxTransaction {
  id: string;
  transactionType: TIndirectTaxTransactionType;
  status: TIndirectTaxTransactionStatus;
  date: string;
  reportingDate?: string;
  source: string;
  sourcePlatformId?: string;
  invoiceNumber?: string;
  marketplace?: string;
  currency: string;
  amount: number;
  taxCollected: number;
  total: number;
  effectiveTaxRate: string;
  customer?: TIndirectTaxTransactionCustomer;

  jurisdictionName: string;
  countryOrTradeBloc: string;
  destinationCountryName: string;
  originCountryName?: string;

  supplyType?: TIndirectTaxTransactionSupplyType;
  documentType?: TIndirectTaxTransactionDocumentType;
  vatStatus?: TIndirectTaxTransactionVatStatus;
  taxableAmount?: number;
  vat?: number;
  grossAmount?: number;
  effectiveVatRate?: string;

  grossSales?: number;
  grossPurchases?: number;
  outputVat?: number;
  inputVat?: number;

  taxBasis?: TIndirectTaxTaxBasis;
  filingCurrency?: string;
  originAddress?: string;
  destinationAddress?: string;
  conversionRate?: number;
  customerId?: string;
  exemptionApplied?: boolean;
  exemptionReason?: string;
  lineItems?: TIndirectTaxTransactionLineItem[];
  entityName?: string;
  entityCountryCode?: string;
}

export interface TIndirectTaxTransactionSummaryConfig {
  label: string;
  key: string;
  isCurrency: boolean;
}

export interface TIndirectTaxTransactionRegionTabConfig {
  value: TIndirectTaxTransactionRegion;
  label: string;
}

export type TIndirectTaxTransactionDetail = TIndirectTaxTransaction & {
  taxBasis: TIndirectTaxTaxBasis;
  filingCurrency: string;
  destinationAddress: string;
  destinationCountryCode?: string;
  exemptionApplied: boolean;
  lineItems: TIndirectTaxTransactionLineItem[];
  entityName: string;
  entityCountryCode: string;
  filings: { id: string; title: string; status: IndirectTaxFilingStatus }[];
};

export interface TIndirectTaxTransactionDetailLineItemConfig {
  taxColumnLabel: string;
  totalColumnLabel: string;
}

export interface TIndirectTaxTransactionDetailSummaryConfig {
  label: string;
  key: keyof TIndirectTaxTransactionDetail;
  isCurrency: boolean;
}
